import { useState, useEffect, Fragment } from 'react';
import { PhotoIcon, UserIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Dialog, Transition } from '@headlessui/react';
import axiosInstance, { handleApiError } from '../utils/axiosConfig.js';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.get(`/users`);
      setUsers(res.data.data);
    } catch (err) {
      const errorInfo = handleApiError(err, 'Failed to fetch users');
      setError(errorInfo.message);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };


  const handleDeleteClick = (userId) => {
    setUserToDelete(userId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    try {
      await axiosInstance.delete(`/users/${userToDelete}`);
      setUsers(users.filter(user => user._id !== userToDelete));
      toast.success('User deleted successfully');
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (err) {
      const errorInfo = handleApiError(err, 'Failed to delete user');
      toast.error(errorInfo.message);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !users.length) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="text-red-600 mb-4">Error loading users</div>
        <button 
          onClick={fetchUsers} 
          className="btn btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
       {/* Header Section */}
       <div className="mb-6">
         <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
       </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <UserIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-white">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase tracking-wider">
                    Profile
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase tracking-wider">
                    Joined Date
                  </th>
                   <th className="px-6 py-3 text-center text-sm font-medium text-black uppercase tracking-wider">
                     Actions
                   </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user, index) => (
                  <tr key={user._id} className="hover:bg-teal-50 border-b border-gray-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img 
                            className="h-10 w-10 rounded-full object-cover"
                            src="/img/admin_image.png" 
                            alt={user.name}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.name || 'No Name'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email || 'No Email'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'admin' 
                          ? 'bg-red-500 text-white' 
                          : 'bg-green-500 text-white'
                      }`}>
                        {user.role || 'User'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                    </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                       <div className="flex space-x-2 justify-center">
                         <button
                           onClick={() => handleViewUser(user)}
                           className="text-teal-600 hover:text-teal-700 px-3 py-2 text-sm font-medium transition-colors flex items-center"
                         >
                           <EyeIcon className="h-4 w-4 mr-1" />
                           View
                         </button>
                         <button
                           onClick={() => navigate('/profile')}
                           className="text-gray-600 hover:text-gray-700 px-3 py-2 text-sm font-medium transition-colors flex items-center"
                         >
                           <PencilIcon className="h-4 w-4 mr-1" />
                           Edit
                         </button>
                         <button
                           onClick={() => handleDeleteClick(user._id)}
                           disabled={user.role === 'admin'}
                           className={`px-3 py-2 text-sm font-medium transition-colors flex items-center ${
                             user.role === 'admin' 
                               ? 'text-gray-400 cursor-not-allowed' 
                               : 'text-red-600 hover:text-red-700'
                           }`}
                         >
                           <TrashIcon className="h-4 w-4 mr-1" />
                           Delete
                         </button>
                       </div>
                     </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Details Modal - Compact Identity Card */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200 relative">
            {/* Cross Button Inside Box */}
            <button
              onClick={() => setShowUserModal(false)}
              className="absolute top-3 right-3 z-10 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
            >
              <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Card Header - Compact */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-4 py-4 text-center relative">
              {/* Profile Photo */}
              <div className="w-16 h-16 mx-auto rounded-full overflow-hidden border-3 border-white shadow-md">
                <img 
                  src="/img/admin_image.png" 
                  alt="Admin Profile" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Name and Role */}
              <h2 className="text-lg font-bold text-white mt-2 mb-1">{selectedUser.name}</h2>
              <div className="inline-flex items-center px-3 py-0.5 bg-white bg-opacity-20 rounded-full">
                <span className="text-white text-xs font-medium capitalize">{selectedUser.role}</span>
              </div>
            </div>

            {/* Card Body - Simple Vertical Layout */}
            <div className="px-4 py-3 bg-white">
              {/* Simple Label-Value Format */}
              <div className="space-y-2">
                {/* Email */}
                <div>
                  <p className="text-sm text-gray-500 font-medium mb-1">EMAIL</p>
                  <p className="text-lg text-gray-900">{selectedUser.email}</p>
                </div>

                {/* Joined Date */}
                <div>
                  <p className="text-sm text-gray-500 font-medium mb-1">JOINED</p>
                  <p className="text-lg text-gray-900">
                    {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: '2-digit'
                    }) : 'Unknown'}
                  </p>
                </div>
              </div>

              {/* Status Badge - Compact */}
              <div className="flex items-center justify-center mt-4">
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  selectedUser.role === 'admin' 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                    selectedUser.role === 'admin' ? 'bg-red-500' : 'bg-green-500'
                  }`}></div>
                  {selectedUser.role === 'admin' ? 'Admin' : 'User'}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Transition.Root show={isDeleteModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleDeleteCancel}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                        <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                          Delete User
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to delete this user? This action cannot be undone.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={handleDeleteConfirm}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={handleDeleteCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default Users;
